"use strict";
/*globals Grid:false, moment:false, BaseAPI: false */

/**
 * Cette classe (GridColumn) définit une colonne pour la grille.
 * Avant de l'utiliser, il faut préciser son comportement, soit de manière "bas niveau", avec les propriétés "content",
 * "groupContent", "align", "compare", "createEditor"..., soit de manière "haut niveau", avec une des méthodes toutes
 * prêtes pour les types courant : 
 * - le plus simple est d'appeler "asText", qui permettra la saisie d'un texte
 * - on peut aussi utiliser "asNumber", "asDate", "asBoolean", "asSelect", "asMultiSelect", qui parlent d'elles-mêmes
 * - il y a des cas plus particulier "asAddRemove", "asPosition", "asAvatar", "asLooping"
 * - il y a enfin dans SOLVNet des cas plus métier "asCustomer", "asProduct", "asMsCustomer", "asExtendedInformationAttachment"...
 */
function GridColumn(name, title, width) {
	// Le nom de la colonne
	this.name = name;

	// Le contenu HTML dans l'en-tête (du DOM, du texte ou une fonction retournant du DOM ou du texte)
	this.title = title;

	// La largeur en pixel souhaitée pour la colonne
	this.width = width;// ne pas forcer de taille par défaut. Sinon, dans les "asXXX", on ne sait pas si on peut proposer une taille par défaut

	// Indique si le tri est possible sur la colonne. Dans ce cas, "compare" doit être définie aussi
	this.sortable = false;

	// Indique si le regroupement est possible sur la colonne. Dans ce cas, "compare" et "groupContent" doivent être définies aussi
	this.groupable = false;

	// Indique si la colonne peut être redimensionnée
	this.resizable = true;

	// Indique si la valeur dans cette colonne peut être modifiée
	this.editable = true;

	// Indique si la valeur dans cette colonne est obligatoire
	this.required = false;

	// Indique si la colonne est masquée pour le moment
	this.hidden = false;

	// Indique si la colonne est prête pour générer le contenu dans la grille (normallement, faut uniquement entre loading() et loaded()
	this.ready = true;

	// L'alignement du contenu dans la cellule (center par défaut car les colonnes personnalisée le sont généralement)
	this.align = 'center';

	// Une fonction prenant 1 ligne en paramètre et retournant le contenu HTML d'une cellule
	// this.content = function(row) -> html

	// Une fonction prenant 1 ligne en paramètre et retournant le contenu HTML du groupe selon cette ligne
	// this.groupContent = function(row) -> html

	// Une fonction prenant 2 lignes en paramètre et retournant <0, 0 ou >0 selon l'ordre dans lequel afficher ces lignes
	// this.compare = function(row1, row2, sortAscending) -> int

	// Une fonction prenant 1 ligne en paramètre et générant dans "parent" le composant permettant la saisie d'une valeur dans la langue "lang"
	// this.createEditor = function(row, parent, lang, required, onchange) -> html

	// Une fonction prenant en paramètre une ligne et un editeur créé par "createEditor" et retournant un éventuel message d'erreur
	// this.validateEditor = function(row, editor) -> error string

	// S'il n'y a pas eu d'erreur, applique à la ligne la valeur saisie dans un editeur créé par "createEditor" et retourne "true" s'il y a eu une modification
	// this.applyEditor = function(row, editor) -> modified?

	// Une fonction sans paramètre retournant le composant permettant de filtrer
	// this.createFilter = function(parent, lang) -> html

	// Une fonction permettant de mettre à jour dynamiquement l'éditeur de filtre créé dans "createFilter" en fonction des lignes affichées ou mono-filtrées
	// this.updateFilter = function(editor, visibleRows, singleFilteredRows) -> void

	// Une fonction prenant en paramètre l'éditeur de filtre créé dans "createFilter" et retournant une fonction de filtre prenant une ligne et retournant true si elle est visble
	// this.prepareRowFilter = function(editor) -> (function(row) -> boolean)

	// Une fonction prenant en paramètre l'éditeur de filtre et retournant une sauvegarde du filtre rapide
	// this.saveFilter = function(editor) -> savedFilter

	// Une fonction appelée au chargement pour demander de restaurer le filtre "savedFilter" dans le composant de filtre rapide "editor"
	// this.loadFilter = function(editor, savedFilter) -> void
}

/** positionne la propriété "width" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setWidth = function(width) {
	this.width = (width === 'auto') ? undefined : width;
	return this;
};

/** positionne la propriété "sortable" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setSortable = function(sortable) {
	this.sortable = (typeof sortable === 'undefined') ? true : sortable;
	return this;
};

/** positionne la propriété "groupable" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setGroupable = function(groupable) {
	this.groupable = (typeof groupable === 'undefined') ? true : groupable;
	return this;
};

/** positionne la propriété "resizable" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setResizable = function(resizable) {
	this.resizable = (typeof resizable === 'undefined') ? true : resizable;
	return this;
};

/** positionne la propriété "editable" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setEditable = function(editable) {
	this.editable = (typeof editable === 'undefined') ? true : editable;
	return this;
};

/** positionne la propriété "required" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setRequired = function(required) {
	this.required = (typeof required === 'undefined') ? true : required;
	return this;
};

/** positionne la propriété "hidden" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setHidden = function(hidden) {
	this.hidden = (typeof hidden === 'undefined') ? true : hidden;
	return this;
};

/** positionne la propriété "align" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setAlign = function(align) {
	this.align = align || 'left';
	return this;
};

/** positionne la propriété "footer" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setFooter = function(footer) {
	this.footer = footer;
	return this;
};

/** positionne la propriété "content" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setContent = function(content) {
	this.content = content;
	return this;
};

/** surcharge la propriété "content" pour appliquer la fonction passée comme pattern décorateur, et renvoie la colonne pour chainer les appels */
GridColumn.prototype.overrideContent = function(overrideContent) {
	var originalContent = this.content;
	this.content = function(row) {
		var c = originalContent(row);
		return overrideContent(c, row) || c;
	};
	return this;
};

/**
 * Cette méthode est appelée par la grille pour vérifier la saisie de l'utilisateur avec "validateEditor".
 * En retour, la colonne appelle "done"avec l'erreur de saisie éventuellement, ou avec le résultat de "applyEditor".
 */
GridColumn.prototype.validateAndApplyEditor = function(row, editor, done) {
	// TODO grille asynchrone
	var error = this.validateEditor(row, editor);
	if (error)
		done(error, false);
	else
		done(null, this.applyEditor(row, editor));
};

/** positionne la propriété "createEditor" et renvoie la colonne pour chainer les appels */
GridColumn.prototype.setEditor = function(createEditor) {
	this.createEditor = createEditor;
	return this;
};

/** surcharge la propriété "createEditor" pour appliquer la fonction passée comme pattern décorateur, et renvoie la colonne pour chainer les appels */
GridColumn.prototype.overrideEditor = function(overrideCreateEditor) {
	var originalCreateEditor = this.createEditor;
	this.createEditor = function(row, parent, lang, required, onchange) {
		originalCreateEditor(row, parent, lang, required, onchange);
		overrideCreateEditor(row, parent, lang, required, onchange);
	};
	return this;
};

/** désactive la saisie sur cette colonne, en vidant les propriétés liées à l'édition, et renvoie la colonne pour chainer les appels */
GridColumn.prototype.noEditor = function() {
	this.createEditor = undefined;
	this.validateEditor = undefined;
	this.applyEditor = undefined;
	this.editable = false;
	this.required = false;
	return this;
};

/** désactive les filtres rapides sur cette colonne, en vidant les propriétés liées à cette fonction, et renvoie la colonne pour chainer les appels */
GridColumn.prototype.noFilter = function() {
	this.createFilter = undefined;
	this.prepareRowFilter = undefined;
	this.saveFilter = undefined;
	this.loadFilter = undefined;
	return this;
};

/**
 * Quand une colonne se charge de manière asynchrone, on peut utiliser le couple "loading" / "loaded"
 * pour surcharger le comportement le temps du chargement.
 *
 * Exemple :
 * var c = new GridColumn(...);  // création de la colonne
 * c.asSelect(...);              // préparation de la colonne
 * c.loading();                  // passer en chargement
 * $.get(...).done(function() {  // chargement asynchrone
 *   ...
 *   c.loaded(grid);             // chargement terminé + maj du contenu de la colonne
 * });
 */
GridColumn.prototype.loading = function() {
	this.ready = false;
	this.internalOverridesWhileLoading();
	return this;
};

/**
 * Cette méthode doit être appelée une fois la colonne chargée SI le chargement s'est fait de manière asynchrone en utilisant "loading".
 *
 * @see GridColumn.prototype.loading pour plus d'info
 */
GridColumn.prototype.loaded = function(grid) {
	this.ready = true;
	if (grid)
		grid.updateCol(this);
	return this;
};

/**
 * Cette méthode va temporairement surcharger certaines méthodes jusqu'à la fin du chargement de la colonne.
 * L'idée générale est de conserver un éventuel filtre rapide sauvegardé pour l'appliquer une fois la colonne chargée.
 * Pour cela, on va surcharger les méthodes "createFilter", "updateFilter", "prepareRowFilter", "loadFilter", "saveFilter", "loaded"
 */
GridColumn.prototype.internalOverridesWhileLoading = function() {
	// Pendant le chargement, on surcharge les méthodes liées aux filtres rapides
	var originalCreateFilter = this.createFilter;
	var originalUpdateFilter = this.updateFilter;
	var originalPrepareRowFilter = this.prepareRowFilter;
	var originalLoadFilter = this.loadFilter;
	var originalSaveFilter = this.saveFilter;
	// Pendant le chargement, on garde les références aux paramètres des appels aux méthodes surchargées
	var tempCreateFilterParent = null;
	var tempCreateFilterLang = null;
	var tempLoadFilterValue = null;
	// On surcharge "createFilter" pour conserver les références sur "parent" et "lang" et créer le composant une fois chargée
	this.createFilter = function(parent, lang) {
		tempCreateFilterParent = parent;
		tempCreateFilterLang = lang;
		// Suite à un appel à "clearFilters", on repasse dans "createFilter" une deuxième fois.
		// Dans ce cas, on jette le filtre rapide qui avait été précédemment mis de côté
		tempLoadFilterValue = null;
	};
	// On désactive updateFilter le temps que les informations ne sont pas chargées.
	this.updateFilter = undefined; 
	// On surcharge "prepareRowFilter" pour désactiver le filtre rapide pendant le chargement (on appellera "updateFilters" plus tard)
	this.prepareRowFilter = function(_editor) {
		// On attend que le chargement soit terminé pour activer les filtres
		// Toutefois, si un filtre rapide doit être chargé, on ne peut pas retourner "null". On retourne alors une fonction qui retourne "true" tout le temps
		if (tempLoadFilterValue)
			return function(_row) { return true; };
		return null;
	};
	// On surcharge "loadFilter" pour conserver les références sur "editor" et "savedFilter" et restaurer le filtre rapide une fois chargée
	this.loadFilter = function(_editor, value) {
		tempLoadFilterValue = value;
	};
	// On surcharge "saveFilter" pour ne pas perdre le filtre du précédent chargement si la colonne n'est pas encore chargée
	this.saveFilter = function(_editor) {
		return tempLoadFilterValue;
	};
	// On surcharge aussi "loaded" pour injecter le filtre rapide quand la colonne est prête
	this.loaded = function(grid) {
		// On restaure les méthodes de sauvegarde/restauration de filtres rapides d'origine
		this.createFilter = originalCreateFilter;
		this.updateFilter = originalUpdateFilter;
		this.prepareRowFilter = originalPrepareRowFilter;
		this.loadFilter = originalLoadFilter;
		this.saveFilter = originalSaveFilter;
		// Créer le composant de filtre rapide maintenant qu'on est prêt
		if (this.createFilter && tempCreateFilterParent) {
			var filter = this.createFilter(tempCreateFilterParent, tempCreateFilterLang);
			if (tempLoadFilterValue) {
				this.loadFilter(filter, tempLoadFilterValue);
				// pas besoin de updateFilters car il sera appelé par "updateCol()"
				// this.updateFilters();
			}
		}
		// On supprime la surcharge de la méthode "loaded"
		delete this.loaded;
		// On appelle la méthode "loaded" d'origine pour que la grille continue normalement
		return this.loaded(grid);
	}.bind(this);
};

/**
 * Cette colonne est faite pour gérer la multi-sélection de ligne. Elle fonctionne de la manière suivante :
 * - chaque ligne aura une coche pour sélectionner ou désélectionner la ligne
 * - l'en-tête sera aussi une coche pour sélectionner ou désélectionner toutes les lignes
 * - le pied de colonne comptera le nombre de lignes sélectionnées
 *
 * @param {String|DOM} gridSelector - un sélecteur jQuery permettant de trouver la grille, par exemple '#grid'
 * @param {String} property - indique la propriété ('id' souvent) à stocker dans la sélection. Sinon, par défaut, la sélection contient les lignes elles-mêmes
 * @param {Boolean} rowSelection - active la sélection des lignes par clic, utile dans le cas de grille où la sélection de ligne n'a pas encore d'impact
 * @param {Function(selection)} onchange - une fonction qui sera appelée à chaque fois que la sélection change, en lui passant le tableau de sélection
 */
GridColumn.prototype.asRowMultiSelection = function(gridSelector, property, rowSelection, onchange) {
	var column = this,
		grid = $(gridSelector),
		selection = [],
		allSelected = false;

	// cette fonction retourne la valeur à conserver dans la sélection pour la ligne "row". Généralement, c'est "row.id".
	function selectionValue(row) {
		if (!row)
			throw new Error('Paramètre manquant "row"');
		if (typeof property === 'string')
			return row[property];
		if (typeof property === 'function')
			return property(row);
		return row;
	}

	// cette fonction affiche dans le pied-de-colonne le nombre de ligne sélectionnées et prévient l'éventuel listener
	function selectionChanged() {
		var text = (selection.length || '').toString(); // pour ne pas afficher les 0
		grid.find('.grid-row-selection-count').text(text);
		if (onchange)
			onchange(selection);
	}

	// cette fonction ajuste le contenu d'un bouton selon qu'il doivent apparaitre comme sélectionné ou non
	function updateCheckbox(button, selected) {
		button.empty().append(selected ? Grid.prototype.defaultOptions.selectionTrueContent : Grid.prototype.defaultOptions.selectionFalseContent);
	}

	// cette fonction gère le clic pour ajouter/retirer la ligne (ou toutes les lignes) de la sélection.
	function handleClick(event) {
		var tr = $(event.target).closest('tr'), // La ligne cliquée
			button = tr.find('.grid-row-selection'), // Le bouton de sélection dans cette ligne
			row = tr.data('row'); // l'objet métier associé

		// Pour annuler la sélection de la ligne
		event.stopPropagation();

		if (row) {
			// Sélectionner / Désélectionner une ligne
			var value = selectionValue(row);
			var index = selection.indexOf(value);
			// Mettre à jour le tableau de sélection
			if (index === -1)
				selection.push(value);
			else
				selection.splice(index, 1);
			// Le bouton sera affiché comme coché si la ligne devient sélectionnée
			updateCheckbox(button, index === -1);

		} else {
			// Tout sélectionner / Tout désélectionner
			allSelected = !allSelected;
			// Mettre à jour le tableau de sélection
			selection = allSelected ? grid.grid().visibleRows.map(selectionValue) : [];
			// Raffraichir toute la colonne
			grid.grid().updateCol(column);
			// Le bouton sera affiché comme coché si allSelected
			updateCheckbox(button, allSelected);
		}

		// Prévenir ceux qui le souhaitent
		selectionChanged();
	}

	grid.on('click', rowSelection ? 'tr.bbm-grid-header-row button.grid-row-selection, tr.bbm-grid-row' : 'button.grid-row-selection', handleClick);

	// Ne pas écraser la largeur demandée, si elle a été précisée dans le constructeur de GridColumn
	column.width = column.width || 60;

	return $.extend(column, {
		resizable: false,
		title: function() {
			var button = $('<button type="button" class="btn btn-link grid-row-selection" />');
			updateCheckbox(button, allSelected);
			button.on('remove', function() {
				grid.off('click', handleClick);
			});
			return button;
		},
		content: function(row) {
			// Création du composant de sélection d'une ligne
			var button = $('<button type="button" class="btn btn-link grid-row-selection" />');
			// Ajout des classes pour gérer la navigation
			button.addClass('bbm-grid-nav-focusable bbm-grid-nav-clickable');
			// Affichage du bon statut pour la checkbox
			updateCheckbox(button, selection.indexOf(selectionValue(row)) !== -1);
			return button;
		},
		footer: '<span class="grid-row-selection-count" />',
		getSelection: function () {
			return selection;
		},
		setSelection: function (s) {
			if (!$.isArray(s))
				throw new Error('s doit être un tableau');
			selection = s;
			grid.grid().updateCol(column);
			selectionChanged();
		},
		clearSelection: function () {
			if (selection.length) {
				selection = [];
				grid.grid().updateCol(column);
				selectionChanged();
			}
		}
	});
};

GridColumn.prototype.asText = function(property, requiredMessage, maxlength, onvalidate, useTextArea, fixedHeight, defaultValue, overrideOnChange) {
	var column = this;
	if (!defaultValue)
		defaultValue = '';
	function value(row) {
		return (row[property] || defaultValue).toString();
	}
	
	function escapeDirtyText(event) { // Voir la fonction homonyme dans XMLUtils.java
		function isForbiddenCharacter(c) {
			if (c === '\t'.charCodeAt() || c === '\n'.charCodeAt() || c === '\r'.charCodeAt()) // 0x9 0xa 0xd
				return false;
			if (c < '\u0020'.charCodeAt() || c === '\uFFFE'.charCodeAt() || c === '\uFFFF'.charCodeAt())
				return true;
			if (c > '\uD7FF'.charCodeAt() && c < '\uE000'.charCodeAt())
				return true;
			return false;
		}

		let val = (event.clipboardData || window.clipboardData).getData('text');
		if (!!val) {
			// Parcourir la chaine de gauche à droite
			let position = 0;
			// Utiliser 'remaining' pour nettoyer au fur et à mesure la partie gauche déjà traitée
			let remaining = val;
			// Utiliser 'keep' pour les parties à conserver
			let keep = "";
			while (position < remaining.length) {
				let c = remaining.charCodeAt(position);
				if (isForbiddenCharacter(c)) {
					// Quand on tombe sur un caractère
					if (position > 0)
						keep += remaining.substring(0, position);
					remaining = remaining.substring(position + 1);
					position = 0;
				} else {
					position++;
				}
			}
			// Si on a eu des modification du texte alors on les appliques à
			if (remaining.length !== val.length) {
				if (remaining.length > 0)
					keep += remaining;
				$(event.target).val(keep);
			} else {
				$(event.target).val(val);
			}
			event.preventDefault();
		}
	}

	column.sortable = true;
	column.groupable = true;
	column.align = 'left';
	column.content = function(row) {
		if (useTextArea) {
			// pour conserver les retours à la ligne
			return $('<pre>' + value(row) + '</pre>')
				.attr('title', value(row));
			}
		return value(row);
	};

	column.groupContent = value;

	column.compare = function(row1, row2, sortAscending) {
		return (sortAscending ? 1 : -1) * value(row1).localeCompare(value(row2));
	};

	column.createEditor = function(row, parent, _lang, required, onchange) {
		// Création du composant de saisie mono ou multi-ligne
		var editor = useTextArea ? $('<textarea />') : $('<input type="text" />');
		// Ajout de 2 classes pour prendre le focus pendant la navigation et sélectionner automatiquement le texte
		editor.addClass('bbm-grid-nav-focusable');
		editor.addClass(fixedHeight ? 'bbm-grid-nav-clickable' : 'bbm-grid-nav-selectable');
		// vérification lors d'une action "coller" ATTENTION : ne pas passer par la méthode on de Jquery 
		editor[0].addEventListener('paste', escapeDirtyText);
		// Finalisation du composant de saisie
		editor.addClass('form-control').prop('required', required).attr('maxlength', maxlength).val(value(row));
		// Ajout dans la cellule
		editor.appendTo(parent);
		column.editor = editor;
		const trElem = parent.closest('tr');
		editor.attr('data-property', property);
		// redimensionner automatiquement le textarea
		if (useTextArea)
			editor.textareaWithModal(() => trElem.find('[data-property="' + property + '"]'));
		// Détection des modifications
		if (onchange)
			editor.on('change', onchange);
		if (overrideOnChange)
			editor.on('change', overrideOnChange);
	};

	column.validateEditor = function(row, editor) {
		var required = editor.prop('required'), val;
		if (required || onvalidate) {
			val = editor.data('textarea') ? editor.data('textarea').val() : (editor.val() ||  '');
			if (required && val.trim().length === 0)
				return requiredMessage;
			if (onvalidate)
				return onvalidate(row, val, column.title);
		}
		return '';
	};

	column.applyEditor = function(row, editor) {
		var oldValue = value(row),
			newValue = editor.data('textarea') ? editor.data('textarea').val() : (editor.val() ||  ''),
			changed = newValue !== oldValue;
		if (changed)
			row[property] = newValue;
		return changed;
	};

	column.createFilter = function(parent, lang) {
		return $('<input type="search" class="form-control" />').attr('placeholder', lang.filterContains).appendTo(parent);
	};

	column.prepareRowFilter = function(editor) {
		var v = editor[0].value;
		if (!v)
			return false; // no filter
		v = v.toLowerCase(); // avant la boucle pour le faire une seule fois
		return function(row) { return value(row).toLowerCase().indexOf(v) >= 0; };
	};

	column.saveFilter = function(editor) {
		return editor.val().toString();
	};

	column.loadFilter = function(editor, value) {
		editor.val(value);
	};

	return this;
};

GridColumn.prototype.asNumber = function(property, decimals, filterType, requiredMessage, lang, onvalidate) {
	var column = this, nanMessage = Grid.prototype.defaultOptions.numberNaNMessage(column.title);
	var isNumberOnIphone =  navigator.userAgent.indexOf('iPhone') >= 0 && decimals !== 0 ;
	/**
	 * @return String formatée au locale de l'utilisateur
	 */
	function value(row) {
		var v = row[property];
		return (typeof v !== 'undefined' && v != null) ? BaseAPI.numberFormatFromLocale(v, decimals, lang) : '';
	}
	/**
	 * @return Number
	 */
	function editValue(editor) {
		var s = editor.val();
		return (s === '') ? undefined : BaseAPI.numberParseFromLocale(s, decimals, lang);
	}

	function checkInputType(input) {
		if(isNumberOnIphone) {
			input.attr('type', 'number');
			// Si on ne précise pas le nombre est considéré comme :invalid
			// et il ne pourrait pas être submit.
			input.attr('step', Math.pow(10,-decimals));
		}
	}

	function buildInput(placeholder, percent) {
		var input = $('<input type="tel" class="form-control" style="text-align: right;" />')
		.attr('placeholder', placeholder)
		.css('width', percent + '%');
		checkInputType(input);
		return input;
	}

	column.width = column.width || (filterType === 'range' ? 120 : 90);
	column.sortable = true;
	column.groupable = true;
	column.align = 'right';
	column.content = value;
	column.groupContent = value;
	column.compare = function(row1, row2, sortAscending) {
		var v1 = row1[property], v1Missing = (typeof v1 === 'undefined' || v1 == null),
			v2 = row2[property], v2Missing = (typeof v2 === 'undefined' || v2 == null);
		if (v1Missing && v2Missing)
			return 0;
		if (v1Missing)
			return sortAscending ? -1 : 1;
		if (v2Missing)
			return sortAscending ? 1 : -1;
		return (sortAscending ? 1 : -1) * (v1 - v2);
	};
	column.createEditor = function(row, parent, _lang, required, onchange) {
		// Création du composant de saisie avec type="tel" pour permettre l'ouverture d'un pavé numérique
		// (type="number" le fait aussi mais nous embête car il refuse les séparateurs de milliers et force le séparateur décimal à ".")
		var editor = $('<input type="tel" class="form-control" style="text-align: right;" />');
		checkInputType(editor);
		// Ajout de 2 classes pour prendre le focus pendant la navigation et sélectionner automatiquement le texte
		editor.addClass('bbm-grid-nav-focusable bbm-grid-nav-selectable');
		// Finalisation du composant de saisie et ajout dans la cellule
		editor.prop('required', required).val(isNumberOnIphone ? row[property] : value(row)).appendTo(parent);
		// Détection des modifications
		if (onchange)
			editor.on('change', onchange);
		column.editor = editor;
	};
	column.validateEditor = function(row, editor) {
		var val = editValue(editor);
		if (typeof val !== 'undefined' && isNaN(val))
			return nanMessage;
		if (editor.prop('required') && (typeof val === 'undefined' || val == null))
			return requiredMessage;
		if (onvalidate)
			return onvalidate(row, val, column.title);
		return '';
	};
	column.applyEditor = function(row, editor) {
		var EPSILON = 0.0001;
		var oldValue = row[property], newValue = editValue(editor);
		row[property] = newValue;

		if(isNaN(oldValue) && isNaN(newValue))
			return false;

		if(isNaN(oldValue) ^ isNaN(newValue))
			return true;

		return Math.abs(oldValue-newValue) >= EPSILON;
	};
	column.createFilter = function(parent, lang) {
		var filter;
		if (filterType === 'range')
			filter = $('<div />').append(buildInput(lang.filterMin, 50)).append(buildInput(lang.filterMax, 50));
		else if (filterType === 'contains')
			filter = buildInput(lang.filterContains, 100);
		else
			filter = buildInput(lang.filterEquals, 100);
		return filter.appendTo(parent);
	};
	column.prepareRowFilter = function(editor) {
		var min, max, filter;
		if (filterType === 'range') {
			// Filtre "min <= value <= max"
			min = editValue(editor.children(':first-child'));
			max = editValue(editor.children(':last-child'));
			if ((typeof min !== 'undefined') || (typeof max !== 'undefined')) {
				return function(row) {
					var v = row[property] || 0;
					return (typeof min === 'undefined' || v >= min) && (typeof max === 'undefined' || v <= max);
				};
			}
		} else if (filterType === 'contains') {
			// Filtre "value contient filtre"
			filter = editor.val() || '';
			if (filter !== '') {
				return function(row) {
					var v = row[property] || 0;
					return v.toString().indexOf(filter) >= 0;
				};
			}
		} else {
			// Filtre "value == filtre"
			filter = editValue(editor);
			if (typeof filter !== 'undefined') {
				return function(row) {
					var v = row[property] || 0;
					return v === filter;
				};
			}
		}
		return false; // no filter
	};

	column.saveFilter = function(editor) {
		if (filterType === 'range') {
			var min = editValue(editor.children(':first-child')),
				max = editValue(editor.children(':last-child'));
			if ((typeof min !== 'undefined') || (typeof max !== 'undefined')) {
				var result = {};
				if (typeof min !== 'undefined')
					result.min = min;
				if (typeof max !== 'undefined')
					result.max = max;
				return result;
			}
			return undefined;
		} else if (filterType === 'contains' || filterType === 'equals') {
			return editValue(editor);
		}
	};

	column.loadFilter = function(editor, value) {
		if (filterType === 'range') {
			editor.children(':first-child').val(value ? value.min : null);
			editor.children(':last-child').val(value ? value.max : null);
		} else {
			editor.val(value);
		}
	};

	return this;
};

GridColumn.prototype.asDate = function(property, format, pickDate, pickTime, requiredMessage, onvalidate, allowNull) {
	var column = this;

	function textValue(row) {
		var v = row[property];
		return v ? moment(v).format(format) : '';
	}

	function getTime(m) {
		if (!pickDate)
			m = m.year(1970).month(0).date(1);
		if (!pickTime)
			m = m.startOf('day');
		return m.toDate().getTime();	
	}

	function getEditor(editor) {
		return editor.children();
	}
	
	function editValue(editor) {
		var v = editor.val();
		if (!v)
			return undefined;
		v = moment(v, format);
		return getTime(v);
	}

	function buildInput(placeholder) {
		return $('<input type="text" class="form-control" style="text-align: right; " />')
			.attr('placeholder', placeholder);
	}

	function calculateDefaultWidth(pickDate, pickTime) {
		if (pickDate && ! pickTime)
			return 260;
		if (pickTime && ! pickDate)
			return 183;
		return 330;
	}

	function adjustFilterComponents(selectCriteria, inputMin, inputMax, inputContains) {
		var crit = selectCriteria.val();
		var showContains = crit === 'contains';
		var showMin = crit === 'minMax' || crit === 'none';
		var	showMax = crit === 'equals' || crit === 'notEquals' || crit === 'minMax' || crit === 'none';

		// MAJ de la vue
		inputContains.toggle(showContains);
		inputMin.toggle(showMin);
		inputMax.toggle(showMax);

		// CSS
		if (showMin && showMax) { // Minmax
			selectCriteria.css('width', '0px');
			inputMin.add(inputMax).css('width', 'calc(50% - 10px)'); // (14px de carret + 5px de margin + 1px de border) / 2 =~ 10px
		} else if (showMax) { // equals, notEquals
			selectCriteria.css('width', '30%');
			inputMax.css('width', 'calc(70% - 5px)');
		} else if (showContains) { // contains
			selectCriteria.css('width', '30%');
		} else { // empty, not empty, vide
			selectCriteria.css('width', '100%');
		}

		// Placeholder à MAJ si le champ max est affiché seul
		inputMax.attr('placeholder', (showMax && !showMin) ? selectCriteria.children(':selected').text() : inputMax.attr('data-default-placeholder'));

		// Raz des inputs sur l'option vide
		if (crit === 'none') {
			inputMin.add(inputMax).add(inputContains).val('');
			selectCriteria.val('minMax');
		}

		// Ajuster la couleur du texte dans le combobox "critère".
		// En fait, quand il est réduit on masque le texte avec une couleur white
		if (showMin && showMax) {
			selectCriteria.css('color', 'white');
		} else {
			selectCriteria.css('color', Grid.prototype.defaultOptions.textColor);
		}
	}

	column.width = column.width || calculateDefaultWidth(pickDate, pickTime);
	column.sortable = true;
	column.groupable = true;
	column.align = 'right';
	column.content = textValue;
	column.groupContent = textValue;
	column.compare = function(row1, row2, sortAscending) {
		var v1 = row1[property];
		var v2 = row2[property];
		if (!v1 && !v2)
			return 0;
		if (!v1 && v2)
			return sortAscending ? -1 : 1;
		if (v1 && !v2)
			return sortAscending ? 1 : -1;
		return (sortAscending ? 1 : -1) * (v1 - v2);
	};
	column.createEditor = function(row, parent, lang, required, onchange) {
		var editor = $('<div />').appendTo(parent);
		// Création du composant de saisie
		var input = $('<input type="text" class="form-control" style="text-align: right; " />');
		// Ajout d'une classe pour indiquer que l'input peut obtenir le focus
		input.addClass('bbm-grid-nav-focusable');
		// Ajout d'une classe pour indiquer de simuler le clic à la prise de focus
		input.addClass('bbm-grid-nav-clickable');
		// Par contre, il ne faut pas sélectionner le texte. Sinon, on voit la sélection malgré le calendrier
		//input.addClass('bbm-grid-nav-selectable');
		// Finalisation du composant de saisie
		input.val(textValue(row)).prop('required', required);
		// Ajout dans la cellule
		input.appendTo(editor);

		const trElem = parent.closest('tr');
		input.attr('data-property', property);

		// Détection des modifications
		if (onchange)
			input.on('change', onchange);
		// Affichage du calendrier sous la forme d'un modal
		input.datepickerWithModal({
			format: format,
			useCurrent: required, // Si c'est obligatoire, useCurrent nous permet de sélectionner la date du jour par défaut si le chamsp reste vide.
			defaultDate: editValue(input),
			locale: lang.momentLocale,
			calendarWeeks: true,
			toolbarPlacement: 'bottom',
			showClear: !required, // La corbeille n'est pas disponible si c'est obligatoire.
			showClose: true,
			showTodayButton: pickDate,
			sideBySide: true,
			timeOnly: pickTime && (!pickDate),
			getEditor: () => trElem.find('[data-property="' + property + '"]')
		});
		column.editor = input;
	};
	column.validateEditor = function(row, editor) {
		var required = getEditor(editor).prop('required'), val;
		if (required || onvalidate) {
			val = editValue(editor.children());
			if (required && (typeof val === 'undefined'))
				return requiredMessage;
			if (onvalidate)
				return onvalidate(row, val, column.title);
		}
		return '';
	};
	column.applyEditor = function(row, editor) {
		var m = row[property] !== undefined ? getTime(moment(row[property])) : undefined;
		var newValue = editValue(getEditor(editor).children());
		row[property] = newValue;
		return m !== newValue;
	};

	column.createFilter = function(parent, lang) {
		var inputContains = buildInput(lang.filterContains).css({ 'width': 'calc(70% - 5px)', 'margin-left': '5px' });
		var inputMin = buildInput(lang.filterMin);
		var inputMax = buildInput(lang.filterMax).css('margin-left', '5px').attr('data-default-placeholder', lang.filterMax);
		var selectCriteria = $(''
			+ '<select class="form-control" style="vertical-align: bottom">'
			+ '  <option value="none"></option>'
			+ '  <option selected="selected" value="minMax">' + lang.filterMinMax + '</option>'
			+ '  <option value="contains">' + lang.filterContains + '</option>'
			+ '  <option value="equals">' + lang.filterEquals + '</option>'
			+ '  <option value="notEquals">' + lang.filterNotEquals + '</option>'
			+ '  <option value="empty">' + lang.filterEmpty + '</option>'
			+ '  <option value="notEmpty">' + lang.filterNotEmpty + '</option>'
			+ '</select>'
		);

		// S'assurer de la couleur des options dans le select (car la couleur du select peut être blanche)
		// https://support.solvnet.fr:9998/devels/edit/3382
		selectCriteria.children().css('color', Grid.prototype.defaultOptions.textColor);
		// Ajuster le contenu de la zone de filtre à la création
		adjustFilterComponents(selectCriteria, inputMin, inputMax, inputContains);
		// Ajuster le contenu de la zone de filtre quand on change le critère
		selectCriteria.on('change', function() {
			adjustFilterComponents(selectCriteria, inputMin, inputMax, inputContains);
		});

		// $.fn.datepickerInModal
		inputMin.add(inputMax).datepickerInModal({
			'format': format,
			'useCurrent': false,
			'locale': lang.momentLocale,
			'toolbarPlacement': 'bottom',
			'showTodayButton': pickDate,
			'showClear': true,
			'showClose': true,
			'sideBySide': true,
			'calendarWeeks': pickDate
		});

		// DOM
		return $('<div/>')
			.append(inputMin)
			.append(selectCriteria)
			.append(inputContains)
			.append(inputMax)
			.appendTo(parent);
	};

	column.prepareRowFilter = function(editor) {
		// Filtre "min <= value <= max"
		var inps = editor.children();
		var contains = $(inps[2]).val(),
			min = editValue($(inps[0])),
			max = editValue($(inps[3])),
			crit = $(inps[1]).val();

		switch (crit) {
			case 'none':
				return false;
			case 'minMax':
				if (typeof min === 'undefined' && typeof max === 'undefined')
					return false;
				return function(row) {
					var v = row[property];
					return ((typeof min === 'undefined' || (!!v && v >= min)) || (!!allowNull && v === undefined))
						&& ((typeof max === 'undefined' || (!!v && v <= max)) || (!!allowNull && v === undefined));
				};
			case 'contains':
				if (typeof contains === 'undefined')
					return false;
				return function(row) { return moment(row[property]).format(format).indexOf(contains) !== -1; };
			case 'equals':
				if (typeof max === 'undefined')
					return false;
				return function(row) { return Math.abs(row[property] - max) < 60000; };
			case 'notEquals':
				if (typeof max === 'undefined')
					return false;
				return function(row) { return typeof row[property] === 'undefined' || Math.abs(row[property] - max) > 60000; };
			case 'empty':
				return function(row) { return typeof row[property] === 'undefined'; };
			case 'notEmpty':
				return function(row) { return typeof row[property] !== 'undefined';	};
		}
	};

	column.saveFilter = function(editor) {
		var min, max, contains,
			inputs = editor.children(),
			criteria = $(inputs[1]).val();
		if (criteria !== 'none') {
			min = $(inputs[0]).val();
			max = $(inputs[3]).val();
			contains = $(inputs[2]).val();
			if (min || max || criteria !== 'minMax')
				return { min: min, max: max, contains: contains, criteria: criteria };
		}
	};

	column.loadFilter = function(editor, value) {
		// Dans cet ordre pour appliquer les values des inputs après leur mise à zéro, cf: createFilter / select.on('change')
		var children = editor.children();
		var selectCriteria = $(children[1]), inputMin = $(children[0]), inputMax = $(children[3]), inputContains = $(children[2]);
		if (value && value.criteria)
			selectCriteria.val(value.criteria)/*.change() non, cf adjustFilterComponents ci-après*/;
		inputContains.val(value ? value.contains : null);
		inputMin.val(value ? value.min : null);
		inputMax.val(value ? value.max : null);
		// Ne pas appeler selectCriteria.change() car ça réapplique les filtre inutilement.
		// On veut juste ajuster la largeur et la visibilité des composants du filtre en fonction du critère
		adjustFilterComponents(selectCriteria, inputMin, inputMax, inputContains);
	};

	return this;
};

GridColumn.prototype.asBoolean = function(property, trueText, falseText, trueContent, falseContent, onvalidate) {
	var column = this;
	trueText = trueText || Grid.prototype.defaultOptions.booleanTrueText;
	falseText = falseText || Grid.prototype.defaultOptions.booleanFalseText;
	trueContent = trueContent || Grid.prototype.defaultOptions.booleanTrueContent;
	falseContent = falseContent || Grid.prototype.defaultOptions.booleanFalseContent;

	function textValue(row) {
		var v = row[property];
		return (v === true) ? trueText : (v === false) ? falseText : '';
	}

	column.width = column.width || 40;
	column.sortable = true;
	column.groupable = true;
	column.align = 'center';
	column.content = function(row) {
		var v = row[property];
		return v ? trueContent : falseContent;
	};
	column.groupContent =  textValue;
	column.compare = function(row1, row2, sortAscending) {
		var b1, b2, v1, v2;
		b1 = row1[property];
		b2 = row2[property];
		v1 = (typeof b1 === 'undefined') ? 0 : (b1 === false) ? 1 : 2;
		v2 = (typeof b2 === 'undefined') ? 0 : (b2 === false) ? 1 : 2;
		return sortAscending ? v1 - v2 : v2 - v1;
	};
	column.createEditor = function(row, parent, _lang, required, onchange) {
		// Création du composant de saisie
		var checkbox = $('<input type="checkbox" />')
			.prop('checked', !!row[property])
			.prop('required', required)
			.appendTo(parent);
		// Détection des modifications
		if (onchange)
			checkbox.on('change', onchange);
		// Activation des "materialCheckbox" si le composant est dispo
		if ($.fn.materialCheckbox)
			checkbox.materialCheckbox({});
		// Récupèration du composant pouvant prendre le focus : la checkbox ou le bouton de "materialCheckbox"
		var component = $.fn.materialCheckbox ? checkbox.parent().find('button') : checkbox;
		// Ajout des classes "bbm-grid-nav-focusable" pour pouvoir prendre le focus et "bbm-grid-nav-clickable" pour basculer la coche
		component.addClass('bbm-grid-nav-focusable bbm-grid-nav-clickable');
		column.editor = component;
	};
	column.validateEditor = function(row, editor) {
		var input = editor.is('.bbm-checkbox') ? editor.children('input') : editor;
		return onvalidate ? onvalidate(row, input.prop('checked'), input.prop('required')) : '';
	};
	column.applyEditor = function(row, editor) {
		var input = editor.is('.bbm-checkbox') ? editor.children('input') : editor;
		var oldValue = !!row[property], newValue = input.prop('checked');
		row[property] = newValue;
		return oldValue !== newValue;
	};
	column.createFilter = function(parent, lang) {
		return $('<select />').gridFilterSelect(parent, lang, [{v: true, t: trueText}, {v: false, t: falseText}], 'v', 't');
	};
	column.prepareRowFilter = function(editor) {
		var f = editor.val();
		if (f === '')
			return false; // no filter
		// utilisation de !!row[property] volontaire :
		// - true, 1, 2, du texte donnent tous 'true'
		// - false, undefined, 0, '' donnent tous 'false'
		// donc les infos booléens comme les colonnes indicateurs 'memo', 'contact', ... pointant sur des nombres fonctionnent aussi
		return function(row) { return (!!row[property]).toString() === f; };
	};

	column.saveFilter = function(editor) {
		return editor.val();
	};

	column.loadFilter = function(editor, value) {
		// Charger la valeur demandée dans le filtre
		editor.val(value);
		// Ne pas appeler .change() car ça réapplique les filtre inutilement.
		editor.gridFilterSelect('refresh');
	};

	return this;
};

GridColumn.prototype.asSelect = function(property, options, optionValueProperty, optionTextProperty, requiredMessage, onvalidate, useNativeCombo, dynamicFilter, defaultTextValue, useImage) {
	var column = this;
	if(!defaultTextValue)
		defaultTextValue = '';
		
	function indexOfValue(value) {
		for (var i = 0; i < options.length; i++) {
			if (options[i][optionValueProperty] === value)
				return i;
		}
		return -1;
	}
	
	function textValue(row) {
		var v = row[property];
		for (var i = 0; i < options.length; i++) {
			if (options[i][optionValueProperty] === v)
				return (typeof optionTextProperty === 'string') ? options[i][optionTextProperty] : optionTextProperty(options[i]);
		}
		return defaultTextValue;
	}

	function editValue(editor) {
		// On utilise "data-option-index" pour stocker l'index dans le tableau d'options
		// - c'est utile si le champ est facultatif car on ajoute une entrée "vide" qui décale
		// - c'est utile si certaines options sont supprimées en fonction des lignes par "overrideEditor"
		// - Autre avantage editor.val() retourne une String => alors options[index][optionValueProperty]  nous retourne la valeur avec le bon type Int ou String en fonction du cas.
		var index = editor.find('option:selected').data('option-index');
		if (index !== undefined && index !== null)
			return options[index][optionValueProperty];
		// uniquement quand le champ est facultatif et que l'utilisateur choisit l'entrée "vide"
		return undefined;
	}

	column.sortable = true;
	column.groupable = !useImage;
	column.align = !useImage ? 'left' : 'center';
	column.content = textValue;
	column.groupContent = textValue;
	column.compare = function(row1, row2, sortAscending) {
		return (sortAscending ? 1 : -1) * (
			!useImage 
				? textValue(row1).localeCompare(textValue(row2))
				: (indexOfValue(row1[property]) - indexOfValue(row2[property])) 
			);
	};
	column.createEditor = function(row, parent, _lang, required, onchange) {
		var value = row[property],
			div = $('<div />').appendTo(parent); // car multiselect a besoin d'un parent

		// Car par défaut, les cellules sont en 'overflow: hidden'
		parent.addClass('overflow');

		var select = $('<select class="form-control" />')
			.prop('required', required)
			.append($('<option value="" />').prop('selected', (typeof value !== 'number') && !value).html('&nbsp;')) // Valeur par défaut, sélectionnée si pas de valeur
			.append($.map(options, function modelOptionToDOMOption(option, optionIndex) {
				var optionText = (typeof optionTextProperty === 'string') ? option[optionTextProperty] : optionTextProperty(option);
				return $('<option />')
					.attr('value', option[optionValueProperty].toString())
					.html(optionText)
					.prop('selected', option[optionValueProperty] === value)
					.data('option-index', optionIndex); // Stocker l'index dans le tableau des options, potentiellement filtré
			}))
			.appendTo(div);

		// Détection des modifications
		if (onchange)
			select.on('change', onchange);

		// Dans certaines grilles, l'utilisation du composant ne convient pas. On préfère garder le combo natif
		if (! useNativeCombo)
			select.multiselect({
				buttonWidth: '100%'
			});
		// Récupèration du composant pouvant prendre le focus : le select ou le bouton de "multiselect"
		var component = useNativeCombo ? select : div.find('button.dropdown-toggle');
		// Ajout des classes "bbm-grid-nav-focusable" pour pouvoir prendre le focus et "bbm-grid-nav-clickable" pour ouvrir la liste
		component.addClass('bbm-grid-nav-focusable bbm-grid-nav-clickable');
		column.editor = select;
	};
	column.validateEditor = function(row, editor) {
		var v = editValue(editor),
			required = editor.children().prop('required');
		if (onvalidate)
			return onvalidate(row, v);
		if (required && v === undefined)
			return requiredMessage;
	};
	column.applyEditor = function(row, editor) {
		var oldValue = row[property], newValue = editValue(editor);
		row[property] = newValue;
		return oldValue !== newValue;
	};
	column.createFilter = function(parent, lang) {
		return !useImage ? $('<select />').gridFilterSelect(parent, lang, options, optionValueProperty, optionTextProperty) : undefined;
	};
	if (dynamicFilter) {
		if (!useImage)
			column.updateFilter = function(editor, visibleRows, singleFilteredRows) {
				// Pour la colonne "asSelect", "editor" pointe sur le "select"
				var select = editor;
				// On conserve la sélection si elle existe
				var selectedVal = select.val() || '';
				// Récupérer les valeurs dans les lignes
				var values = {};
				visibleRows.forEach(function(r) {
					var value = (typeof r[property] === 'number') ? r[property] : (r[property] || '');
					if (value === '' && r.extendedInformation)
						value = (typeof r.extendedInformation[property] === 'number') ? r.extendedInformation[property] : (r.extendedInformation[property] || '');
					values[value.toString()] = true;
				});
				// Si un filtre est actif, on autorise les options correspondants aux lignes filtrées uniquement par cette colonne
				if (selectedVal !== '') {
					var selectedOption = select.children('option:selected');
					var selectedOptionIndex = selectedOption[0].getAttribute('data-option-index');
					var selectedOptionValue = options[selectedOptionIndex - 1][optionValueProperty];
					singleFilteredRows.forEach(function(r) {
						var v = r[property];
						if (v !== undefined && v !== null && v !== selectedOptionValue)
							values[v.toString()] = true;
					});
				}
				// Récupérer toutes les options, stockées dans "data" ou, la première fois, en enfant de "select"
				var allOptions = select.data('options');
				if (allOptions === undefined) {
					allOptions = select.children().get();
					select.data('options', allOptions);
				}
				// Filtrer les options
				select.empty().append(allOptions.filter(function(option, _index) {
					// L'option est-elle visible ?
					return (option.value === '') || (values[option.value] === true && values.hasOwnProperty(option.value));
				}));
				// On réinjecte la sélection
				select.val(selectedVal);
			};
	}
	column.prepareRowFilter = function(editor) {
		if (!useImage) {
			// Récupérer l'option et sa position initiale dans la liste des options
			var option = editor.children('option:selected');
			var index = option.length === 0 ? -1 : option[0].getAttribute('data-option-index');
			if (index <= 0)
				return false; // -1 ou 0 , il n'y a pas de filtre actif
			var value = options[index - 1][optionValueProperty];
			return function(row) { return row[property] === value; };
		}
	};
	column.saveFilter = function(editor) {
		return !useImage ? editor.find(':selected').val() : undefined;
	};
	column.loadFilter = function(editor, value) {
		if (!useImage) {
			// Charger la valeur demandée dans le filtre
			editor.val(value);
			// Ne pas appeler .change() car ça réapplique les filtre inutilement.
			editor.gridFilterSelect('refresh');
		}
	};

	return this;
};

GridColumn.prototype.asMultiSelect = function(property, options, optionValueProperty, optionTextProperty, nonSelectedText, allSelectedText, selectAllText, requiredMessage, dynamicFilter, onvalidate) {
	var column = this;

	function textValue(row) {
		var values = row[property] || [];
		if (values.length === 0 || optionValueProperty === optionTextProperty)
			return values.join(', ');
		return $.map(values, function(value, _valueIndex) {
			for (var i = 0; i < options.length; i++) {
				if (options[i][optionValueProperty] === value)
					return options[i][optionTextProperty].replace(/ /g, '&nbsp;');
			}
			return '';
		}).join(', ');
	}

	function editValue(editor) {
		var values = editor.find("select").val() || [];
		return $.map(values, function(value, _valueIndex) {
			for (var i = 0; i < options.length; i++) {
				if (options[i][optionValueProperty].toString() === value)
					return options[i][optionValueProperty]; // as a typed object
			}
			return undefined;
		});
	}

	column.align = 'left';
	column.content = textValue;
	column.groupContent = textValue;
	column.compare = function(row1, row2, sortAscending) {
		return (sortAscending ? 1 : -1) * textValue(row1).localeCompare(textValue(row2));
	};
	column.createEditor = function(row, parent, _lang, required, onchange) {
		var value = row[property] || [],
			div = $('<div />').appendTo(parent); // car multiselect a besoin d'un parent

		// Car par défaut, les cellules sont en 'overflow: hidden'
		parent.addClass('overflow');

		var select = $('<select class="form-control" multiple="multiple" />')
			.prop('required', required)
			.append($.map(options, function modelOptionToDOMOption(option, _index) {
				return $('<option value="' + option[optionValueProperty].toString() + '">' + option[optionTextProperty] + '</option>').prop('selected', value.indexOf(option[optionValueProperty]) >= 0);
			}))
			.appendTo(div)
			.multiselect({
				buttonWidth: '100%',
				nonSelectedText: nonSelectedText,
				numberDisplayed: 100,
				nSelectedText: '',
				allSelectedText: allSelectedText,
				includeSelectAllOption: !!selectAllText,
				selectAllText: selectAllText,
				maxHeight: 300
			});

		// Bloquer les "focusout" pour ne pas perturber la sauvegarde de la grille et rendre le focus au bouton
		var button = div.find('.dropdown-toggle');
		div.find('.dropdown-menu').on('focusin', function() {
			button.focus();
		});
		div.find('.dropdown-menu').on('focusout', function() {
			return false;
		});

		// Détection des modifications
		if (onchange)
			select.on('change', onchange);
		// Ajout des classes "bbm-grid-nav-focusable" pour pouvoir prendre le focus et "bbm-grid-nav-clickable" pour ouvrir la liste
		div.find('button.dropdown-toggle').addClass('bbm-grid-nav-focusable bbm-grid-nav-clickable');
		column.editor = div;
	};

	column.validateEditor = function(row, editor) {
		var val, required = editor.find('select').prop('required');
		if (required || onvalidate) {
			val = editValue(editor);
			if (required && val.length === 0)
				return requiredMessage;
			if (onvalidate)
				return onvalidate(row, val, column.title);
		}
		return '';
	};

	column.applyEditor = function(row, editor) {
		var oldValue = row[property] || [], newValue = editValue(editor), modified = oldValue.length !== newValue.length;
		row[property] = newValue;
		if (!modified) {
			for (var i = 0; i < oldValue.length; i++) {
				if (newValue.indexOf(oldValue[i]) < 0) {
					modified = true;
					break;
				}
			}
		}
		return modified;
	};

	column.createFilter = function(parent, lang) {
		return $('<select />').gridFilterSelect(parent, lang, options, optionValueProperty, optionTextProperty);
	};

	if (dynamicFilter) {
		column.updateFilter = function(editor, visibleRows, singleFilteredRows) {
			// Pour la colonne "asMultiSelect", "editor" pointe sur le "select"
			var select = editor;
			// On conserve la sélection si elle existe
			var selectedVal = select.val() || '';
			// Récupérer les valeurs dans les lignes
			var values = {};
			visibleRows.forEach(function(r) {
				var arr = r[property] || [];
				if (arr.length === 0)
					arr = r.extendedInformation && r.extendedInformation[property] ? r.extendedInformation[property] : [];
				arr.forEach(function(value) { values[value.toString()] = true; });
			});
			// Si un filtre est actif, on autorise les options correspondants aux lignes filtrées uniquement par cette colonne
			if (selectedVal !== '') {
				var selectedOption = select.children('option:selected');
				var selectedOptionIndex = selectedOption[0].getAttribute('data-option-index');
				var selectedOptionValue = options[selectedOptionIndex - 1][optionValueProperty];
				singleFilteredRows.forEach(function(r) {
					var v = r[property];
					if ($.isArray(v) && v.indexOf(selectedOptionValue) === -1) {
						v.forEach(function(value) {
							values[value.toString()] = true;
						});
					}
				});
			}
			// Récupérer toutes les options, stockées dans "data" ou, la première fois, en enfant de "select"
			var allOptions = select.data('options');
			if (allOptions === undefined) {
				allOptions = select.children().get();
				select.data('options', allOptions);
			}
			// Filtrer les options
			select.empty().append(allOptions.filter(function(option, _index) {
				// L'option est-elle visible ?
				return (option.value === '') || (values[option.value] === true && values.hasOwnProperty(option.value));
			}));
			// On réinjecte la sélection
			select.val(selectedVal);
		};
	}

	column.prepareRowFilter = function(editor) {
		// Récupérer l'option et sa position initiale dans la liste des options
		var option = editor.children('option:selected');
		var index = option.length === 0 ? -1 : option[0].getAttribute('data-option-index');
		if (index <= 0)
			return false; // -1 ou 0 , il n'y a pas de filtre actif
		var value = options[index - 1][optionValueProperty];
		return function(row) {
			return (row[property] || []).indexOf(value) !== -1;
		};
	};

	column.saveFilter = function(editor) {
		return editor.find(':selected').val();
	};

	column.loadFilter = function(editor, value) {
		// Charger la valeur demandée dans le filtre
		editor.val(value);
		// Ne pas appeler .change() car ça réapplique les filtre inutilement.
		editor.gridFilterSelect('refresh');
	};

	return this;
};

/** Une colonne affichant un bouton "+" dans le titre et un bouton "-" pour chaque ligne */
GridColumn.prototype.asAddRemove = function(gridSelector, oncandelete, oncreateitem, ondeleteitem) {
	// Récupérer le div qui contiendra la grille
	var grid = $(gridSelector),
		// récupérer la colonne
		column = this;

	// Si aucune fonction "ondeleteitem" n'est passée, il n'y aura pas de confirmation et la suppression sera immédiate
	ondeleteitem = ondeleteitem || (function(_item, callback) { callback(true); });
	// Si aucune fonction "oncreateitem" n'est passée, on utilise une fonction par défaut toute simple
	oncreateitem = oncreateitem || (function(callback) { callback({}); });

	// Nom de la colonne
	column.name = column.name || 'addremove';
	// En-tête de colonne = bouton "Ajouter"
	column.title = column.title || $('<div style="text-align:center;"><a class="add" href="#"><span class="glyphicon glyphicon-plus text-success"></span></a></div>');
	// Largeur de 30 pixels
	column.width = column.width || 30;
	// Contenu de la colonne = bouton "Supprimer"
	column.content = function(row) {
		if (oncandelete && !oncandelete(row))
			return '';
		return '<a class="remove" href="#"><span class="glyphicon glyphicon-trash text-danger"></span></a>';
	};

	// Gérer le clic sur le bouton "Add"
	grid.on('click', '.add', function() {
		// "oncreateitem" peut être asynchrone, si elle a besoin de données du serveur.
		// C'est pourquoi on appelle "oncreateitem" en lui passant une fonction de callback.
		// La fonction de callback sera elle-même appelée en retour avec l'élément créé.
		// Le booléen "first" indique si l'appelant souhaite que l'ajout se fasse au début ou à la fin de la liste
		grid.grid().saveEditing().then(function() {
			oncreateitem(function(item, first) {
				grid.grid().addRow(item, first);
			});
		});
	});

	// Gérer le clic sur un des boutons "Remove"
	grid.on('click', '.remove', function() {
		var item = $(this).closest('tr').data('row');
		ondeleteitem(item, function(removeFromArray) {
			if (removeFromArray)
				grid.grid().removeRow(item);
			else
				grid.grid().updateFilters();
		});
	});

	return this;
};

/** Une colonne affichant une pastille de couleur et permettant de sélectionner une couleur */
GridColumn.prototype.asColorButton = function(property, onchange) {
	var column = this;

	function updateColor(a, row) {
		var intColor = row[property],
			htmlColor = 'rgb(' + (intColor >> 16 & 255) + ',' + (intColor >> 8 & 255) + ',' + (intColor & 255) + ')';
		a.attr('data-color', htmlColor).css('background-color', htmlColor);
	}

	function selectColor(a) {
		if (typeof a.colorpicker === 'undefined') {
			window.alert('Plugin "colorpicker" non défini');
		} else {
			a.colorpicker({
				format: 'rgb',
				color: a.attr('data-color')
			}).on('hidePicker', function(hidePickerEvent) {
				var item = a.closest('tr').data('row'),
					rgbColor = hidePickerEvent.color.toRGB(),
					intColor = (rgbColor.r * 256 + rgbColor.g) * 256 + rgbColor.b;
				if (item[property] !== intColor) {
					item[property] = intColor;
					updateColor(a, item);
					if (onchange)
						onchange(item);
				}
				a.colorpicker('destroy');
			}).colorpicker('show');
		}
	}

	column.width = column.width || 40;
	column.defaultClick = selectColor;
	column.content = function(row) {
		var a = $('<a href="#" />').css('display', 'block').css('border-radius', '50%').css('width', '24px').css('height', '24px');
		if (column.editable)
			a.click(function(event) {
				event.stopPropagation();
				selectColor(a);
			});
		updateColor(a, row);
		return a;
	};

	return this;
};

/** Une colonne affichant une image sélectionnée par l'utilisateur, sur le principe des avatars */
GridColumn.prototype.asAvatar = function(property, defaultImage, onchange) {
	var column = this;

	// La classe de l'input, utiliser à la création et pour s'assurer qu'une seule input existe à un instant donné
	var inputClassName = 'avatar-input';

	// Supprimer l'input ajoutée précédemment
	var input = $(document.body).children('.' + inputClassName);
	if (input.length > 0)
		input.remove();

	// Créer une input correcte avec les nouveaux paramètres
	input = $('<input type="file" accept="image/jpeg,image/png" style="display: none; " />').addClass(inputClassName).appendTo(document.body).on('change', function oninputfilechange() {
		// 'this' est un input[file], qui pointe sur l'image (par data)
		var img = $($(this).data('img'));
		if (this.files.length > 0 && this.files[0]) {
			var reader  = new FileReader();
			reader.onloadend = function () {
				setImage(img, reader.result, reader.result);
			};
			reader.readAsDataURL(this.files[0]);
			this.value = this.defaultValue;
		}
	});

	// Changer (ou vider) la valeur de l'avatar dans l'objet métier (row), dans l'IHM (img et a) et prévenir un "onchange" éventuel
	function setImage(img, url, value) {
		var row = img.closest('tr').data('row');
		img.attr('src', url);
		img.next().toggle(!!value);
		row[property] = value;
		row.gridModifiedTimestamp = Date.now();
		if (onchange)
			onchange(row);
	}

	// Quand on clique sur l'image, on stocke dans l'input l'image cliqué (pour plus tard) et on fait apparaitre la boite de sélection
	function selectImage(event) {
		event.stopPropagation();
		input.data('img', event.target).click();
	}

	// Quand on clique sur la croix en dessous de l'input, on vide la valeur, on cache la croix et on affiche l'image par défaut
	function clearImage(event) {
		var img = $(event.target).closest('a').prev();
		event.stopPropagation();
		setImage(img, defaultImage, '');
	}

	// Pour chaque ligne, on affiche un div content une "img" (cliquable pour choisir) et un "a" (cliquable pour vider)
	column.content = function(row) {
		var div = $('<div />');
		var img = $('<img />').css('max-height', '32px').css('max-width', '32px').attr('src', row[property] || defaultImage).appendTo(div);
		if (column.editable) {
			img.click(selectImage); // Choisir l'image quand on clique sur l'image
			$('<a href="#" style="display: block;"><span class="glyphicon glyphicon-remove" /></a>').appendTo(div)
				.click(clearImage) // Vider l'image quand on clique sur le lien
				.toggle((row[property] || '').length > 0); // Ne l'afficher que si la ligne a une image;
		}
		return div;
	};

	return this;
};

GridColumn.prototype.asButton = function(nameAndButtonClass, buttonIcon, buttonTitle, options) {
	var column = this;

	// Si le bouton lance une action en javascript (et non pas une URL)
	if (!options.urlPattern) {
		$(options.gridSelector).on('click', '.' + nameAndButtonClass, function() {
			options.onclickbutton($(this), options.gridSelector);
		});
	}

	column.name = column.name || nameAndButtonClass;
	column.title = column.title || '';
	column.content = function(row) {
		var visible = options.oncanshow ? options.oncanshow(row) : true;
		if (!visible)
			return '';
		return $('<a />')
			.attr('class', nameAndButtonClass)
			.attr('href', options.urlPattern ? options.urlPattern.replace('%ID%', row.id) : '#')
			.attr('target', options.urlTarget ? options.urlTarget : '_self')
			.attr('data-id', row.id)
			.attr('title', buttonTitle || '')
			.data('row',row)
			.append($('<span class="material-icons">'+buttonIcon+'</span>'));
	};

	return this;
};

GridColumn.prototype.asMappedObject = function(transformMethod) {
	/*
	 * Le code ci-dessous encapsule les fonctions de la colonne prenant en paramètre une (ou 2) ligne(s)
	 * de manière à les appeler avec un (ou 2) objet(s) dérivé(s) de la (des) ligne(s), par exemple
	 * "row.calculatedInformations" ou "row.customer.extendedInformations".
	 *
	 * Par exemple :
	 * - content(row)->html est surchargée en content(transformMethod(row))->html
	 * - compare(row1, row2, ascDesc)->int est surchargée en compare(transformMethod(row1), transformMethod(row2), ascDesc)->int
	 * - createEditor(row, parent, lang, required, onchange)->html est surchargée en createEditor(transformMethod(row), parent, lang, required, onchange)->html
	 */
	var column = this;
	function delegateMethod(methodName, transformSecondParam) {
		var originalMethod = column[methodName];
		if (originalMethod) {
			column[methodName] = function(/*on utilise "arguments" car il y a un nombre arbitraire d'arguments*/) {
				var params = Array.prototype.slice.call(arguments, 0, arguments.length);
				params[0] = transformMethod(params[0]);
				if (transformSecondParam)
					params[1] = transformMethod(params[1]);
				return originalMethod.apply(column, params);
			};
		}
	}
	delegateMethod('content', false);
	delegateMethod('groupContent', false);
	delegateMethod('compare', true);
	delegateMethod('createEditor', false);
	delegateMethod('validateEditor', false);
	delegateMethod('applyEditor', false);

	// Pour updateFilter, c'est différent.
	// Ca n'est pas une fonction prenant une ligne mais un tableau de lignes
	// On surcharge pour passer un tableau de lignes "transformées" par transformMethod
	var originalUpdateFilter = column.updateFilter;
	if (originalUpdateFilter) {
		column.updateFilter = function(editor, visibleRows, singleFilteredRows) {
			originalUpdateFilter(editor, visibleRows.map(transformMethod), singleFilteredRows.map(transformMethod));
		};
	}

	// Pour prepareRowFilter, c'est aussi différent.
	// Ca n'est pas une function(row) mais une fonction retournant une function(row)
	// On surcharge pour transformer avec "transformMethod" chaque ligne testée
	var originalPrepareRowFilter = column.prepareRowFilter;
	if (originalPrepareRowFilter) {
		column.prepareRowFilter = function(editor) {
			var originalRowFilter = originalPrepareRowFilter(editor);
			if (!originalRowFilter)
				return false;
			return function(row) {
				return originalRowFilter(transformMethod(row));
			};
		};
	}
	return this;
};

GridColumn.prototype.asLooping = function(gridSelector, property, options, optionValueProperty, optionContentProperty, enableHtml) {
	var column = this;

	if (!options ||  options.length < 3)
		throw new Error('Le tableau des différents états est obligatoire.');

	optionValueProperty = optionValueProperty || 'value';
	optionContentProperty = optionContentProperty || 'content';

	function indexOfValue(value) {
		for (var i = 0; i < options.length; i++) {
			if (options[i][optionValueProperty] === value)
				return i;
		}
		return -1;
	}

	function textContent(row) {
		var value = row[property];
		var index = indexOfValue(value);
		return (index >= 0) ? options[index][optionContentProperty] : '';
	}

	column.width = column.width || 40;
	column.sortable = true;
	column.groupable = true;
	column.align = 'center';
	column.content = function(row) {
		return '<div>' + textContent(row) + '</div>';
	};
	column.groupContent = textContent;
	column.compare = function(row1, row2, sortAscending) {
		// on tri par ordre des valeurs dans le tableau des options plutôt par celui de l'HTML, qui ne veut rien dire
		return (sortAscending ? 1 : -1) * (indexOfValue(row1[property]) - indexOfValue(row2[property]));
	};
	column.createEditor = function(row, parent, _lang, _required, onchange) {
		var value = row[property];
		var index = Math.max(indexOfValue(value), 0);
		var option = options[index];
		// Création du composant de saisie, passant de valeur et valeur par clic
		var result = $('<button type="button" class="btn btn-link" />').attr('data-index', index).addClass(property);
		// Ajout du contenu HTML (si le contenu est bien contrôlé) ou text (si le contenu vient de l'utilisateur)
		if (enableHtml)
			result.html(option[optionContentProperty]);
		else
			result.text(option[optionContentProperty]);
		// Ajout de 2 classes pour prendre le focus pendant la navigation et simuler le clic si besoin
		result.addClass('bbm-grid-nav-focusable bbm-grid-nav-clickable');
		// Détection des modifications
		if (onchange)
			result.on('change', onchange);
		// Ajout dans la cellule
		result.appendTo(parent);
		column.editor = result;
	};
	column.validateEditor = function(_row, _editor) {
		return '';
	};
	column.applyEditor = function(row, editor) {
		var oldIndex = indexOfValue(row[property]),
			newIndex = parseInt(editor.attr('data-index')),
			modified = (newIndex !== oldIndex);
		if (modified)
			row[property] = options[newIndex][optionValueProperty];
		return modified;
	};

	if (column.editable)
		$(gridSelector).on('click', '.' + property, function (event) {
			var $editor = $(event.target).closest('.' + property);
			var newIndex = ($editor.attr('data-index') + 1) % options.length;
			var option = options[newIndex];
			$editor.empty();
			$editor.append(option[optionContentProperty]);
			$editor.attr('data-index', newIndex).change();
			$editor.trigger('loop');
		});
	return this;
};

/**
 * Un type de colonne, en lecture seule OBLIGATOIREMENT, permettant de créer une colonne classique
 * avec asText, asNumber, ... mais avec une propriété calculée qui est mise à jour dynamiquement
 * avant chaque accès pour être sûr que le calcul est à jour.
 *
 * Par exemple, la colonne présence de linéaire est :
 * new GridColumn('linear_details.presence', 'Présence', 40)
 * 	.asBoolean('presence', 'Oui', 'Non', '', '', null)
 * 	.asCalculatedObject('presence', function(row) { return LinearAPI.checkLinearDetailPresence(row, infos); }); //main.js
 *
 * TODO retoucher asCalculatedObject quand "property" sera une propriété de GridColumn
 * (à ce moment là, on pourra utiliser "column.property" au lieu de "property"
 */
GridColumn.prototype.asCalculatedObject = function(property, calculatedFunction) {
	var column = this;

	// Les méthodes "content", "groupContent" et "compare" accèdent à la propriété.
	// On les surcharge pour mettre à jour les valeurs avant d'y accéder.
	function delegateMethod(methodName, transformSecondParam) {
		var originalMethod = column[methodName];
		if (originalMethod) {
			column[methodName] = function(row, secondRowOrUndefined) {
				row[property] = calculatedFunction(row);
				if (transformSecondParam)
					secondRowOrUndefined[property] = calculatedFunction(secondRowOrUndefined);
				// Appeler "originalMethod" en utilisant "arguments" au cas où il y a plus de 2 paramètres
				// return originalMethod(row, secondRowOrUndefined);
				return originalMethod.apply(column, arguments);
			};
		}
	}
	delegateMethod('content', false);
	delegateMethod('groupContent', false);
	delegateMethod('compare', true);

	// Les colonnes calculées sont en lecture seule.
	// On pourrait faire "editable.editable = false" mais on prend le risque d'un bug de gestion des droits qui le repasserait à true.
	// On supprime donc les méthodes "createEditor", "validateEditor" et "applyEditor" pour rendre la colonne incapable de créer un éditeur.
	column.noEditor();

	// Pour updateFilter, c'est différent.
	// Ca n'est pas une fonction prenant une ligne mais un tableau de lignes
	// On surcharge pour pré-calculer en amont pour toutes les lignes
	var originalUpdateFilter = column.updateFilter;
	if (originalUpdateFilter) {
		column.updateFilter = function(editor, visibleRows, singleFilteredRows) {
			visibleRows.forEach(function(row) {
				row[property] = calculatedFunction(row);
			});
			singleFilteredRows.forEach(function(row) {
				row[property] = calculatedFunction(row);
			});
			originalUpdateFilter(editor, visibleRows, singleFilteredRows);
		};
	}

	// Pour prepareRowFilter, c'est aussi différent.
	// Ca n'est pas une function(row) mais une fonction retournant une function(row)
	// On surcharge pour pré-calculer en amont pour chaque ligne testée
	var originalPrepareRowFilter = column.prepareRowFilter;
	if (originalPrepareRowFilter) {
		column.prepareRowFilter = function(editor) {
			var originalRowFilter = originalPrepareRowFilter(editor);
			if (!originalRowFilter)
				return false;
			return function(row) {
				row[property] = calculatedFunction(row);
				return originalRowFilter(row);
			};
		};
	}
	return this;
};

/**
 * Une colonne permettant de trier les lignes d'une grille
 */
GridColumn.prototype.asPosition = function(gridSelector, property, emptyLabel, callback) {

	var column = this,
		columnCaption = column.title,
		grid = $(gridSelector);
		
	function getPositionValue() {
		var tab = [];
		grid.grid().visibleRows.forEach(function(row) {
			if (row[property])
				tab.push(row[property]);
		});
		tab.sort(function(a, b) { return a - b; });
		
		var nextAvailablePos = 1;
		while(tab.length > 0 && nextAvailablePos >= tab[nextAvailablePos - 1]) {
			nextAvailablePos++;
		}
		return nextAvailablePos;
	}
	
	function sortArrayByProperty(array, property) {
		function compare(a,b) {
			if (a[property] < b[property])
				return -1;
			if (a[property] > b[property])
				return 1;
			return 0;
		}
		array.sort(compare);
		return array;
	}
	
	// Cette fonction permet d'ajuster la position des lignes suivantes ayant une position par rapport à la ligne en cours
	function ajustNextOtherPositions(row) {
		var newPosition = row[property];
		sortArrayByProperty(grid.grid().visibleRows.filter(function (r) { return !!r[property] && r !== row && r[property] >= row[property]; }), property)
			.forEach(function(r) {
				if (r[property] && r[property] >= newPosition);
					r[property] = ++newPosition;
					r.gridModifiedTimestamp = Date.now();
			});
		row.gridModifiedTimestamp = Date.now();
		grid.grid().updateCol(column);
		callback(grid, undefined);
	}

	function showInput(event) {
		var span = $(event.target).closest('span'),
			label = span.find('label'),
			input = span.find('input').last();
			
		label.css('display', 'none');
		input.val(label.text() !== emptyLabel ? label.text() : '').focus();
		input.css('display', '').focus();
	}
	
	function hideInput(event) {
		var input = $(event.target),
			span = input.closest('span'),
			label = span.find('label'),
			checkbox = span.find('input').first(),
			row = input.closest('tr').data('row'),
			oldPosition = row[property],
			selected = !!input.val(),
			position = parseInt(input.val());
		
		if (oldPosition !== position) {
			row[property] = selected ? position : undefined;
			label.css('display', '');
			label.text(input.val() || emptyLabel);
			input.css('display', 'none');
			checkbox.prop('checked', selected);
			ajustNextOtherPositions(row);
		}
	}
	
	// cette fonction gère le clic pour ajouter/retirer la position de la ligne (ou toutes les lignes).
	function handleClick(event) {
		var input = $.fn.materialCheckbox ? $(event.target).closest('span').find('input').first() : $(event.target), // la checkbox de la ligne
			tr = input.closest('tr'), // La ligne cliquée
			row = tr.data('row'), // l'objet métier associé	
			selected = input.prop('checked'), // l'état de la checkbox
			oneModified = false; // est-ce qu'au moins une position a changé ?

		if (row) {
			// une ligne, on récupère la position maximale dans les lignes visibles et l'on incrémente la valeur de 1 avant de l'assigner à la ligne en cours
			row[property] = selected ? getPositionValue() : undefined;
			row.gridModifiedTimestamp = Date.now();
			oneModified = true;
			input.closest('td').find('label').text(row[property] || emptyLabel);
		} else if (! selected) {
			// vider la colonne position => une ligne pas encore marquée modifiée le sera si elle contenait une position
			grid.grid().visibleRows.forEach(function(row) {
				oneModified |= (typeof row[property] === 'number');
				if (typeof row[property] === 'number')
					row.gridModifiedTimestamp = Date.now();
				delete row[property];
			});
		} else {
			// numéroter la colonne position en écrasant => une ligne pas encore marquée modifiée le sera si elle contenait une position différente
			grid.grid().visibleRows.forEach(function(row, index) {
				var position = index + 1;
				oneModified |= (position !== row[property]);
				if (position !== row[property])
					row.gridModifiedTimestamp = Date.now();
				row[property] = position;
			});
		}	
		// Raffraichir toute la colonne
		if (oneModified && !row)
			grid.grid().updateCol(column);
		// Prévenir du changement
		if (oneModified)
			callback(grid, row);
		// Pour annuler l'action de tri sur la colonne ou la sélection de la ligne lors d'un clic sur la checkbox.
		event.stopPropagation();
	}
	
	function getContainer(labelValue, checkboxValue, addInput, classToAdd) {
		if (!grid.hasClass('bbm-grid-without-editable-position') && column.editable) {
			var container = $('<span style="display:inline;" />'),
				label = $('<label />'),
				checkbox = $('<input type="checkbox" />').prop('checked', checkboxValue).appendTo(container);
			if ($.fn.materialCheckbox)
				checkbox.materialCheckbox({});
			var component = $.fn.materialCheckbox ? checkbox.parent().find('button') : checkbox;
			component.css('width', '20%').css('float', 'left');
			component.on('click', handleClick);
			if (classToAdd)
				component.addClass(classToAdd);
			label.text(labelValue);
			// si on a affaire a un champ de la grille (pas l'entete), on ajoute une zone de saisie
			if (addInput) { 
				var input = $('<input type="tel" class="form-control" maxlength="3" style="display:none; width:50%;"/>');
				input.on('change', hideInput).appendTo(container);
				label.on('dblclick', showInput);
			}
			label.appendTo(container);
			return container;
		}
		return labelValue;
	}

	// Ne pas écraser la largeur demandée, si elle a été précisée dans le constructeur de GridColumn
	column.width = column.width || 150;

	return $.extend(column, {
		resizable: true,
		sortable: true,
		title: function() {
			return getContainer(columnCaption, false, false);
		},
		content: function(row) {
			return getContainer(row[property] || emptyLabel, row[property] != null, true, 'bbm-grid-nav-focusable');
		},
		compare: function(row1, row2, sortAscending) {
			// on trie par ordre des valeurs mais de façon à avoir tj à la fin les valeurs vide quelque soit le type de tri, ascendant ou descendant.
			var v1 = row1[property];
			var v2 = row2[property];
			if (!v1 && !v2)
				return 0;
			if (!v1)
				return 1;
			if (!v2)
				return -1;
			return (sortAscending ? 1 : -1) * (v1 - v2);
		},
		
	});
};

GridColumn.prototype.asBooleanAlwaysActive = function(gridSelector, property, isEditable, callback) {
	var column = this;
	var grid = $(gridSelector);
	column.isEditable =  isEditable;
	
	function handleClick(event) {
		var input = $(event.target); // la checkbox de la ligne
		var tr = input.closest('tr'); // La ligne cliquée
		var row = tr.data('row'); // l'objet métier associé	
		var selected = input.prop('checked');
		
		row[property] = selected;
		row.gridModifiedTimestamp = Date.now();
		callback(grid, row);
		// Pour annuler l'action de tri sur la colonne ou la sélection de la ligne lors d'un clic sur la checkbox.
		event.stopPropagation();
	}
	
	function getContent(checkboxValue, classToAdd) {
		var checkbox = $('<input type="checkbox" />').prop('disabled', !column.isEditable);
		if ($.fn.materialCheckbox)
			checkbox.materialCheckbox({});
		checkbox.on('click', handleClick).prop('checked', checkboxValue);
		if (classToAdd)
			checkbox.addClass(classToAdd);
		return checkbox;
	}

	column.createFilter = function(parent, lang) {
		return $('<select />').gridFilterSelect(parent, lang, [{v: true, t: Grid.prototype.defaultOptions.booleanTrueText}, 
			{v: false, t: Grid.prototype.defaultOptions.booleanFalseText}], 'v', 't');
	};
	column.prepareRowFilter = function(editor) {
		var f = editor.val();
		if (f === '')
			return false; // no filter
		// utilisation de !!row[property] volontaire :
		// - true, 1, 2, du texte donnent tous 'true'
		// - false, undefined, 0, '' donnent tous 'false'
		// donc les infos booléens comme les colonnes indicateurs 'memo', 'contact', ... pointant sur des nombres fonctionnent aussi
		return function(row) { return (!!row[property]).toString() === f; };
	};
	
	return $.extend(column, {
		resizable: true,
		sortable: true,
		content: function(row) {			
			return getContent(row[property], row[property] != null, 'bbm-grid-nav-focusable bbm-grid-nav-clickable');
		},
		compare: function(row1, row2, sortAscending) {
			var b1, b2, v1, v2;
			b1 = row1[property];
			b2 = row2[property];
			v1 = (typeof b1 === 'undefined') ? 0 : (b1 === false) ? 1 : 2;
			v2 = (typeof b2 === 'undefined') ? 0 : (b2 === false) ? 1 : 2;
			return sortAscending ? v1 - v2 : v2 - v1;
		},	
	});
};

GridColumn.prototype.asAvailabilities = function(shortDays, firstDayOfWeek, options) {

	function addDay(caption, okAM, okPM, wrapper) {
		var $dayList = wrapper;
		var $day = $('<li class="day" />').text(caption).appendTo($dayList);
		// on ajoute les images des disponibilités
		if(!okAM && !okPM)
			$day.addClass("bbm-availabilities-not-available");
		if (!okAM && okPM)
			$day.addClass("bbm-availabilities-available-afternoon");
		if (okAM && !okPM)
			$day.addClass("bbm-availabilities-available-morning");
		if (okAM && okPM)
			$day.addClass("bbm-availabilities-available-all-day");
	}
	
	let column = this;

	$(options.gridSelector).on('click', '.button-availabilities', function() {
		options.onclickbutton($(this), options.gridSelector);
	});

	column.align = 'left';
	column.name = column.name;
	column.title = column.title || '';
	column.content = function(row) {
		let el = $('<div class="bbm-availabilities"/>');
		
		$('<a />')
			.attr('class', 'button-availabilities')
			.attr('href', '#')
			.attr('target', '_self')
			.attr('data-id', row.id)
			.attr('title', options.buttonTitle || '')
			.data('row',row)
			.append($('<span class="material-icons">event_available</span>'))
			.appendTo(el);
		$('<ul class="bbm-availabilities-days" />').appendTo(el);

		/** Les disponibilités sont sous ce format là : 
				[
					{"dayId": 2, "availabilities": [{"startTime": 14400000, "endTime": 25200000}]},
					{"dayId": 3, "availabilities": [{"startTime": 14400000, "endTime": 25200000}, {"startTime": 61200000, "endTime": 75600000}]}
				]
		 */
		let availabilities = row.availabilitiesByDay;
		for (let i = 0; i < 7; i++) {
			const idx = (firstDayOfWeek + i) % 7;
			let okAM = false, 
				okPM = false;
			
			let dayAvailability = availabilities.find(e => e['dayId'] === idx);
			if (!!dayAvailability) {
				okAM = dayAvailability.availabilities.length === 2 || dayAvailability.availabilities[0].endTime <= 39600000;
				okPM = dayAvailability.availabilities.length === 2 || dayAvailability.availabilities[0].endTime > 39600000;
			}
			addDay(shortDays[idx].charAt(0), okAM, okPM, el.find('ul'));
		}
		return el;
	};

	return this;
};

/** Un pied-de-colonne qui encapsule un autre pied-de-colonne pour filtrer les lignes non visibles */

function GridFooterVisible(delegate) {
	this.delegate = delegate;
}
GridFooterVisible.prototype.initialize = function(column) {
	this.delegate.initialize(column);
};
GridFooterVisible.prototype.add = function(column, row, visible) {
	if (visible)
		this.delegate.add(column, row, visible);
};
GridFooterVisible.prototype.finalize = function(column, cell, rows) {
	this.delegate.finalize(column, cell, rows);
};

/** Un pied-de-colonne qui décore un autre pied-de-colonne pour ajouter de l'HTML avant ou après */

function GridFooterDecorate(delegate, htmlToPrepend, htmlToAppend) {
	this.delegate = delegate;
	this.htmlToPrepend = htmlToPrepend;
	this.htmlToAppend = htmlToAppend;
}
GridFooterDecorate.prototype.initialize = function(column) {
	this.delegate.initialize(column);
};
GridFooterDecorate.prototype.add = function(column, row, visible) {
	this.delegate.add(column, row, visible);
};
GridFooterDecorate.prototype.finalize = function(column, cell, rows) {
	this.delegate.finalize(column, cell, rows);
	$(cell).prepend(this.htmlToPrepend).append(this.htmlToAppend);
};

/** Un pied-de-colonne qui compte les lignes */

function GridFooterRowCount() {
}
GridFooterRowCount.prototype.initialize = function(_column) {
	this.result = 0;
};
GridFooterRowCount.prototype.add = function(_column, _row, _visible) {
	this.result++;
};
GridFooterRowCount.prototype.finalize = function(_column, cell, _rows) {
	cell.innerHTML = this.result.toString();
};

/** Un pied-de-colonne qui compte les lignes dont la propriété est alimentée (ni les undefined, ni les false, ni les 0, ni les '') */

function GridFooterCount(property) {
	this.property = property;
}
GridFooterCount.prototype.initialize = function(_column) {
	this.result = 0;
};
GridFooterCount.prototype.add = function(_column, row, _visible) {
	if (row[this.property])
		this.result++;
};
GridFooterCount.prototype.finalize = function(_column, cell, _rows) {
	cell.innerHTML = this.result.toString();
};

/** Un pied-de-colonne qui cumule les nombres de la colonne */

function GridFooterNumber(property, resultProperty, decimals) {
	this.property = property;
	this.resultProperty = resultProperty; // count, sum, min, max, avg
	this.decimals = decimals;
}
GridFooterNumber.prototype.initialize = function(_column) {
	this.count = 0;
	this.sum = 0;
	this.min = undefined;
	this.max = undefined;
	this.avg = undefined;
};
GridFooterNumber.prototype.add = function(_column, row, _visible) {
	var val = row[this.property];
	if (typeof val !== 'undefined') {
		this.count++;
		this.sum += val;
		if ((typeof this.min === 'undefined') || (this.min > val))
			this.min = val;
		if ((typeof this.max === 'undefined') || (this.max < val))
			this.max = val;
		this.avg = this.sum / this.count;
	}
};
GridFooterNumber.prototype.finalize = function(_column, cell, _rows) {
	cell.innerHTML = (this[this.resultProperty] || 0).toFixed(this.decimals || 0);
};
